import { cloneDeep, isEqual } from 'lodash';

import { renderLegacyIdoHtmlLabel } from 'src/components/ido/util/renderLegacyIdoHtmlLabel';

import { IdoRecord } from 'src/models/ido';
import {
	IdoFieldContactPerson,
	IdoFieldLastContactDate,
	IdoFieldMultipleChoice,
	IdoFieldNextContactDate,
	IdoFieldNextContactTime,
	IdoFieldNextContactTimeCheckbox,
	IdoFieldSingleChoice,
	IdoFieldTextArea,
	IdoFieldValue,
	IdoFieldValueFaxAction,
	IdoFieldValueLinkAction,
	IdoFieldValueMailAction,
	IdoFieldValuePhoneAction,
} from 'src/models/ido/fieldTypes';

type IdoMergeableField =
	| IdoFieldContactPerson
	| IdoFieldLastContactDate
	| IdoFieldMultipleChoice
	| IdoFieldNextContactDate
	| IdoFieldNextContactTime
	| IdoFieldNextContactTimeCheckbox
	| IdoFieldSingleChoice
	| IdoFieldTextArea
	| IdoFieldValue
	| IdoFieldValueFaxAction
	| IdoFieldValueLinkAction
	| IdoFieldValueMailAction
	| IdoFieldValuePhoneAction;

type IdoMergeableFieldValue = string | string[] | undefined;
type IdoMergeableDisplayValue = {
	display: string;
	value: string;
};

const LEGACY_CHOICE_ITEM_SUFFIX_PATTERN = /#NETZkultur_IdoModel_FieldTypes_Items_ChoiceItem_\d+$/;

const getFieldValue = (field: IdoMergeableField): IdoMergeableFieldValue => {
	switch (field.type) {
		case 'contactPerson':
		case 'singleChoiceSelect':
		case 'singleChoiceRadio':
			return field.selectedValue;
		case 'multiplechoice':
		case 'multiplechoiceTag':
		case 'checkboxgroup':
			return field.selectedValues;
		default:
			return field.value;
	}
};

const setFieldValue = (field: IdoMergeableField, value: IdoMergeableFieldValue): void => {
	switch (field.type) {
		case 'contactPerson':
			if (typeof value === 'string') {
				field.selectedValue = value;
			}
			break;
		case 'singleChoiceSelect':
		case 'singleChoiceRadio':
			if (typeof value === 'string' || value === undefined) {
				field.selectedValue = value;
			}
			break;
		case 'multiplechoice':
		case 'multiplechoiceTag':
		case 'checkboxgroup':
			if (Array.isArray(value)) {
				field.selectedValues = value;
			}
			break;
		default:
			if (typeof value === 'string') {
				field.value = value;
			}
			break;
	}
};

const displayTextForMerge = (value: unknown): string => {
	if (value === null || value === undefined) {
		return '';
	}
	if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
		return '';
	}

	return renderLegacyIdoHtmlLabel(value)
		.replace(/<[^>]*>/g, '')
		.replace(/\s+/g, ' ')
		.trim();
};

const valuesForMerge = (field: IdoMergeableField): readonly IdoMergeableDisplayValue[] | undefined => {
	if (!('values' in field)) {
		return undefined;
	}

	const values = (field as { values?: readonly IdoMergeableDisplayValue[] }).values;
	return Array.isArray(values) ? values : undefined;
};

const displayValueMatchForMerge = (
	field: IdoMergeableField,
	selectedValue: string
): IdoMergeableDisplayValue | undefined => {
	const values = valuesForMerge(field);
	const legacyChoiceItemSuffix = selectedValue.match(LEGACY_CHOICE_ITEM_SUFFIX_PATTERN)?.[0];
	const exactValueMatch = values?.find((value) => value.value === selectedValue);
	if (exactValueMatch !== undefined) {
		return exactValueMatch;
	}

	const legacyChoiceItemMatch = values?.find(
		(value) => legacyChoiceItemSuffix !== undefined && value.value.endsWith(legacyChoiceItemSuffix)
	);
	if (legacyChoiceItemMatch !== undefined) {
		return legacyChoiceItemMatch;
	}

	const displayMatches = values?.filter((value) => displayTextForMerge(value.display) === selectedValue) ?? [];
	return displayMatches.length === 1 ? displayMatches[0] : undefined;
};

const normalizeSelectedValueForMerge = (field: IdoMergeableField, selectedValue: string): string => {
	return displayValueMatchForMerge(field, selectedValue)?.value ?? selectedValue;
};

const normalizeFieldValueForMerge = (
	value: IdoMergeableFieldValue,
	field: IdoMergeableField
): IdoMergeableFieldValue => {
	if (valuesForMerge(field) === undefined) {
		return value;
	}

	if (Array.isArray(value)) {
		return value
			.filter((selectedValue) => selectedValue !== '')
			.map((selectedValue) => normalizeSelectedValueForMerge(field, selectedValue))
			.sort();
	}

	if (value === '') {
		return undefined;
	}

	return value === undefined ? undefined : normalizeSelectedValueForMerge(field, value);
};

const fieldValuesAreEqual = (
	firstValue: IdoMergeableFieldValue,
	secondValue: IdoMergeableFieldValue,
	field: IdoMergeableField
): boolean => {
	return isEqual(normalizeFieldValueForMerge(firstValue, field), normalizeFieldValueForMerge(secondValue, field));
};

class IdoMerger {
	public static merge(base: IdoRecord | null, local: IdoRecord | null, remote: IdoRecord): IdoRecord | null {
		if (!base || !local) {
			return remote;
		}

		const mergedRecord = cloneDeep(remote);

		const baseModel = base.jsonModel?.model;
		const localModel = local.jsonModel?.model;
		const remoteModel = remote.jsonModel?.model;
		const mergedModel = mergedRecord.jsonModel?.model;
		let hasLocalRemoteCollision = false;

		if (baseModel && localModel && remoteModel && mergedModel) {
			const allBaseFields = [...baseModel.leftColumn, ...baseModel.rightColumn];

			allBaseFields.forEach((baseField) => {
				const localField = [...localModel.leftColumn, ...localModel.rightColumn].find(
					(f) => f.name === baseField.name
				);
				const remoteField = [...remoteModel.leftColumn, ...remoteModel.rightColumn].find(
					(f) => f.name === baseField.name
				);
				const mergedField = [...mergedModel.leftColumn, ...mergedModel.rightColumn].find(
					(f) => f.name === baseField.name
				);

				if (!localField || !remoteField || !mergedField) {
					return;
				}

				const baseValue = getFieldValue(baseField);
				const localValue = getFieldValue(localField);
				const remoteValue = getFieldValue(remoteField);

				const localHasChanged = !fieldValuesAreEqual(localValue, baseValue, remoteField);
				const remoteHasChanged = !fieldValuesAreEqual(remoteValue, baseValue, remoteField);

				if (localHasChanged && !remoteHasChanged) {
					setFieldValue(mergedField, localValue);
				}

				if (localHasChanged && remoteHasChanged && !fieldValuesAreEqual(localValue, remoteValue, remoteField)) {
					setFieldValue(mergedField, localValue);
					hasLocalRemoteCollision = true;
				}
			});
		}

		if (hasLocalRemoteCollision && mergedRecord.jsonModel && local.jsonModel) {
			mergedRecord.jsonModel.lastKnownChangeTimestamp = local.jsonModel.lastKnownChangeTimestamp;
		}

		if (mergedRecord.jsonModel && remote.jsonModel?.model.contactPersons) {
			mergedRecord.jsonModel.model.contactPersons = remote.jsonModel.model.contactPersons;
		}

		return mergedRecord;
	}
}

export { IdoMerger };
